import { useEffect, useId, useRef, useState } from 'react';
import type { ReactElement } from 'react';

type MultiSelectComboboxProps<TOption extends string> = {
  label: string;
  options: ReadonlyArray<TOption>;
  selectedValues: ReadonlyArray<TOption>;
  allSelectedLabel: string;
  onSelectedValuesChange: (values: ReadonlyArray<TOption>) => void;
  containerClassName?: string;
  buttonClassName?: string;
  menuClassName?: string;
  optionClassName?: string;
};

export function MultiSelectCombobox<TOption extends string>({
  label,
  options,
  selectedValues,
  allSelectedLabel,
  onSelectedValuesChange,
  containerClassName = 'shrink-0 flex flex-col text-xs font-semibold uppercase tracking-wide text-slate-700',
  buttonClassName = 'flex w-full items-center justify-between rounded-md border border-slate-300 bg-white px-2 py-1.5 text-left text-sm font-normal normal-case tracking-normal text-slate-700 shadow-sm outline-none ring-sky-200 transition focus:border-sky-400 focus:ring',
  menuClassName = 'absolute right-0 z-20 mt-1 w-max min-w-full rounded-md border border-slate-200 bg-white p-2 shadow-lg',
  optionClassName = 'flex cursor-pointer items-center gap-2 whitespace-nowrap rounded px-2 py-1 text-sm normal-case text-slate-700 hover:bg-slate-50',
}: MultiSelectComboboxProps<TOption>): ReactElement {
  const generatedId = useId();
  const comboboxId = `multiselect-combobox-${generatedId}`;
  const menuId = `${comboboxId}-options`;

  const rootRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    function handleOutsidePointerDown(event: MouseEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleOutsidePointerDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsidePointerDown);
    };
  }, []);

  function toggleOption(option: TOption): void {
    if (selectedValues.includes(option)) {
      if (selectedValues.length === 1) {
        return;
      }

      onSelectedValuesChange(selectedValues.filter((selectedValue) => selectedValue !== option));
      return;
    }

    onSelectedValuesChange([...selectedValues, option]);
  }

  function getDisplayLabel(): string {
    return selectedValues.length === options.length ? allSelectedLabel : selectedValues.join(', ');
  }

  return (
    <div ref={rootRef} className={containerClassName}>
      {label}
      <div className="relative mt-1">
        <button
          type="button"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={menuId}
          onClick={() => {
            setIsOpen((previous) => !previous);
          }}
          className={buttonClassName}
        >
          <span>{getDisplayLabel()}</span>
          <span className="ml-2 text-slate-500">▾</span>
        </button>

        {isOpen ? (
          <div id={menuId} className={menuClassName}>
            {options.map((option) => {
              const isSelected = selectedValues.includes(option);

              return (
                <label key={option} className={optionClassName}>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {
                      toggleOption(option);
                    }}
                  />
                  <span>{option}</span>
                </label>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}
